import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Store, ShoppingCart, Users, FileText, RefreshCw, Loader2, Plus,
  Edit3, Trash2, Eye, EyeOff, Package, DollarSign, CheckCircle2,
  XCircle, ArrowUpRight, Send, Tag, BookOpen, UploadCloud, Clock,
  Search, Truck, Settings, Percent, Zap, FileSpreadsheet, Download,
  BarChart3, Wand2, AlertTriangle, TrendingUp
} from 'lucide-react';

export default function WixHub() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Wix Hub');
  const [view, setView] = useState('products');
  const [loading, setLoading] = useState(true);
  const [wixStatus, setWixStatus] = useState(null);

  // Products
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [productForm, setProductForm] = useState({ name: '', description: '', price: '', sku: '', quantity: '', visible: true, productType: 'physical' });
  const [editingProduct, setEditingProduct] = useState(null);
  const [productLog, setProductLog] = useState([]);
  const [creatingProduct, setCreatingProduct] = useState(false);

  // Orders
  const [orders, setOrders] = useState([]);
  const [fulfillForm, setFulfillForm] = useState({ tracking_number: '', carrier: '' });
  const [fulfillingOrder, setFulfillingOrder] = useState(null);

  // Contacts
  const [contacts, setContacts] = useState([]);
  const [contactLabels, setContactLabels] = useState([]);
  const [syncingContacts, setSyncingContacts] = useState(false);
  const [syncingToWix, setSyncingToWix] = useState(false);

  // Blog
  const [blogPosts, setBlogPosts] = useState([]);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [blogForm, setBlogForm] = useState({ title: '', content: '', publish: false });
  const [creatingPost, setCreatingPost] = useState(false);

  // Sync Log
  const [syncLog, setSyncLog] = useState([]);

  // Automation
  const [couponConfig, setCouponConfig] = useState(null);
  const [dispatchConfig, setDispatchConfig] = useState(null);
  const [generatedCoupons, setGeneratedCoupons] = useState([]);
  const [dispatchLog, setDispatchLog] = useState([]);
  const [csvText, setCsvText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importLog, setImportLog] = useState([]);
  const [processingOrders, setProcessingOrders] = useState(false);

  // SEO
  const [seoAudit, setSeoAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [rewriting, setRewriting] = useState({});
  const [rewrites, setRewrites] = useState({});
  const [applying, setApplying] = useState({});
  const [blogTopic, setBlogTopic] = useState('');
  const [blogKeywords, setBlogKeywords] = useState('');
  const [generatingBlog, setGeneratingBlog] = useState(false);
  const [generatedBlog, setGeneratedBlog] = useState(null);
  const [bulkRewriting, setBulkRewriting] = useState(false);
  const [bulkRewrites, setBulkRewrites] = useState([]);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/api/wix/status');
      setWixStatus(data);
    } catch (e) { console.error("WixHub fetch error:", e); } finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (wixStatus && !loading) loadProducts();
  }, [wixStatus, loading]);

  const loadProducts = async () => {
    try {
      const [prodRes, collRes, logRes] = await Promise.all([
        api.get('/api/wix/products?limit=100'),
        api.get('/api/wix/collections').catch(() => ({ data: { collections: [] } })),
        api.get('/api/wix/product-log'),
      ]);
      setProducts(prodRes.data.products || []);
      setCollections(collRes.data.collections || []);
      setProductLog(logRes.data.logs || []);
    } catch (e) { console.error("WixHub error:", e); }
  };

  const loadOrders = async () => {
    try {
      const { data } = await api.get('/api/wix/orders?limit=50');
      setOrders(data.orders || []);
    } catch (e) { console.error("WixHub error:", e); }
  };

  const loadContacts = async () => {
    try {
      const [contactRes, labelRes] = await Promise.all([
        api.get('/api/wix/contacts?limit=100'),
        api.get('/api/wix/contacts/labels').catch(() => ({ data: { labels: [] } })),
      ]);
      setContacts(contactRes.data.contacts || []);
      setContactLabels(labelRes.data.labels || []);
    } catch (e) { console.error("WixHub error:", e); }
  };

  const loadBlog = async () => {
    try {
      const { data } = await api.get('/api/wix/blog/posts');
      setBlogPosts(data.posts || []);
    } catch (e) { console.error("WixHub error:", e); }
  };

  const loadSyncLog = async () => {
    try {
      const { data } = await api.get('/api/wix/sync/log');
      setSyncLog(data.logs || []);
    } catch (e) { console.error("WixHub error:", e); }
  };

  const loadAutomation = async () => {
    try {
      const [couponRes, dispatchRes, couponsRes, dispLogRes, impLogRes] = await Promise.all([
        api.get('/api/wix/automation/coupon-config'),
        api.get('/api/wix/automation/dispatch-config'),
        api.get('/api/wix/automation/coupons'),
        api.get('/api/wix/automation/dispatch-log'),
        api.get('/api/wix/automation/import-log'),
      ]);
      setCouponConfig(couponRes.data.config || {});
      setDispatchConfig(dispatchRes.data.config || {});
      setGeneratedCoupons(couponsRes.data.coupons || []);
      setDispatchLog(dispLogRes.data.logs || []);
      setImportLog(impLogRes.data.logs || []);
    } catch (e) { console.error("WixHub error:", e); }
  };

  const saveCouponConfig = async () => {
    try { await api.put('/api/wix/automation/coupon-config', couponConfig); toast.success('Coupon config saved'); } catch { toast.error('Save failed'); }
  };

  const saveDispatchConfig = async () => {
    try { await api.put('/api/wix/automation/dispatch-config', dispatchConfig); toast.success('Dispatch config saved'); } catch { toast.error('Save failed'); }
  };

  const processOrders = async () => {
    setProcessingOrders(true);
    try {
      const { data } = await api.post('/api/wix/automation/process-orders');
      toast.success(`Processed: ${data.processed} dispatches, skipped: ${data.skipped}`);
      loadAutomation();
    } catch { toast.error('Processing failed'); } finally { setProcessingOrders(false); }
  };

  const importCsv = async () => {
    if (!csvText.trim()) { toast.error('Paste CSV data first'); return; }
    setImporting(true);
    try {
      const { data } = await api.post('/api/wix/automation/import-csv', { csv_data: csvText });
      toast.success(`Imported ${data.created} products, ${data.failed} failed`);
      setCsvText('');
      loadAutomation();
      loadProducts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); } finally { setImporting(false); }
  };

  const loadCsvTemplate = async () => {
    try {
      const { data } = await api.get('/api/wix/automation/csv-template');
      setCsvText(data.template);
    } catch (e) { console.error("WixHub error:", e); }
  };

  const runSeoAudit = async () => {
    setAuditLoading(true);
    try {
      const { data } = await api.get('/api/seo/audit');
      setSeoAudit(data);
    } catch { toast.error('Audit failed'); } finally { setAuditLoading(false); }
  };

  const rewriteProduct = async (productId, name, description) => {
    setRewriting(prev => ({...prev, [productId]: true}));
    try {
      const { data } = await api.post('/api/seo/rewrite', { product_id: productId, current_name: name, current_description: description });
      setRewrites(prev => ({...prev, [productId]: data.optimized}));
    } catch { toast.error('Rewrite failed'); } finally { setRewriting(prev => ({...prev, [productId]: false})); }
  };

  const applySeoRewrite = async (productId) => {
    const opt = rewrites[productId];
    if (!opt) return;
    setApplying(prev => ({...prev, [productId]: true}));
    try {
      const { data } = await api.post('/api/seo/apply', { product_id: productId, name: opt.optimized_title, description: opt.optimized_description });
      if (data.success) { toast.success('SEO applied to Wix!'); setRewrites(prev => { const n = {...prev}; delete n[productId]; return n; }); }
      else { toast.error(data.error || 'Apply failed'); }
    } catch { toast.error('Apply failed'); } finally { setApplying(prev => ({...prev, [productId]: false})); }
  };

  const runBulkRewrite = async () => {
    setBulkRewriting(true);
    try {
      const { data } = await api.post('/api/seo/bulk-rewrite', { max: 10 });
      setBulkRewrites(data.rewrites || []);
      toast.success(`Generated ${data.total} AI rewrites`);
    } catch { toast.error('Bulk rewrite failed'); } finally { setBulkRewriting(false); }
  };

  const generateBlog = async () => {
    if (!blogTopic) { toast.error('Enter a topic'); return; }
    setGeneratingBlog(true);
    try {
      const { data } = await api.post('/api/seo/generate-blog', { topic: blogTopic, keywords: blogKeywords.split(',').map(k => k.trim()).filter(Boolean), publish: false });
      setGeneratedBlog(data.blog);
      toast.success('Blog post generated!');
    } catch { toast.error('Generation failed'); } finally { setGeneratingBlog(false); }
  };

  // Product CRUD
  const createProduct = async () => {
    if (!productForm.name) { toast.error('Product name required'); return; }
    setCreatingProduct(true);
    try {
      const payload = { ...productForm, price: parseFloat(productForm.price) || 0, quantity: parseInt(productForm.quantity) || 0, trackInventory: !!productForm.quantity };
      const { data } = await api.post('/api/wix/products', payload);
      if (data.success) {
        toast.success(`Product "${productForm.name}" created on eigooinc.net!`);
        setShowCreateProduct(false);
        setProductForm({ name: '', description: '', price: '', sku: '', quantity: '', visible: true, productType: 'physical' });
        loadProducts();
      } else { toast.error(data.error || 'Create failed'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setCreatingProduct(false); }
  };

  const updateProduct = async (id) => {
    if (!editingProduct) return;
    try {
      const { data } = await api.put(`/api/wix/products/${id}`, editingProduct);
      if (data.success) { toast.success('Product updated'); setEditingProduct(null); loadProducts(); }
      else { toast.error(data.error || 'Update failed'); }
    } catch { toast.error('Update failed'); }
  };

  const deleteProduct = async (id, name) => {
    if (!window.confirm(`Delete "${name}" from eigooinc.net?`)) return;
    try {
      const { data } = await api.delete(`/api/wix/products/${id}`);
      if (data.success) { toast.success('Product deleted'); loadProducts(); }
      else { toast.error(data.error || 'Delete failed'); }
    } catch { toast.error('Delete failed'); }
  };

  // Order Fulfillment
  const fulfillOrder = async (orderId) => {
    try {
      const { data } = await api.put(`/api/wix/orders/${orderId}/fulfillment`, fulfillForm);
      if (data.success) { toast.success(`Order fulfilled${fulfillForm.tracking_number ? ` — tracking: ${fulfillForm.tracking_number}` : ''}`); setFulfillingOrder(null); setFulfillForm({ tracking_number: '', carrier: '' }); loadOrders(); }
      else { toast.error(data.error || 'Fulfillment failed'); }
    } catch { toast.error('Fulfillment failed'); }
  };

  // Contact Sync
  const syncContactsToLocal = async () => {
    setSyncingContacts(true);
    try {
      const { data } = await api.post('/api/wix/sync/contacts');
      toast.success(`Synced ${data.synced} contacts, skipped ${data.skipped}`);
      loadSyncLog();
    } catch { toast.error('Sync failed'); } finally { setSyncingContacts(false); }
  };

  const syncToWix = async () => {
    setSyncingToWix(true);
    try {
      const { data } = await api.post('/api/wix/sync/techops-to-wix');
      toast.success(`Pushed ${data.synced} contacts to Wix, skipped ${data.skipped}`);
      loadSyncLog();
    } catch { toast.error('Sync failed'); } finally { setSyncingToWix(false); }
  };

  // Blog
  const createBlogPost = async () => {
    if (!blogForm.title) { toast.error('Title required'); return; }
    setCreatingPost(true);
    try {
      const { data } = await api.post('/api/wix/blog/posts', blogForm);
      if (data.success) {
        toast.success(data.published ? 'Post published on eigooinc.net!' : 'Draft saved on Wix');
        setShowBlogForm(false);
        setBlogForm({ title: '', content: '', publish: false });
        loadBlog();
      } else { toast.error(data.error || 'Create failed'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setCreatingPost(false); }
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="wix-hub">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2"><Store className="w-6 h-6 text-[#D4A843]" /> Wix Hub</h1>
              <p className="text-sm text-gray-500 mt-1">Manage eigooinc.net products, orders, contacts, and blog from TechOps</p>
            </div>
            <div className="flex items-center gap-2">
              {wixStatus?.connected && <span className="px-2.5 py-1 rounded-lg text-[10px] font-bold bg-emerald-100 text-emerald-700">Wix Connected</span>}
              {!wixStatus?.connected && <span className="px-2.5 py-1 rounded-lg text-[10px] font-bold bg-red-100 text-red-700">Wix Disconnected</span>}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200 pb-0 overflow-x-auto">
            {[
              { k: 'products', l: 'Products', icon: Package },
              { k: 'orders', l: 'Orders', icon: ShoppingCart },
              { k: 'contacts', l: 'Contacts', icon: Users },
              { k: 'blog', l: 'Blog', icon: BookOpen },
              { k: 'seo', l: 'SEO', icon: TrendingUp },
              { k: 'automation', l: 'Automation', icon: Zap },
              { k: 'sync', l: 'Sync Log', icon: RefreshCw },
            ].map(t => (
              <button key={t.k} onClick={() => { setView(t.k); if (t.k === 'products' && products.length === 0) loadProducts(); if (t.k === 'orders' && orders.length === 0) loadOrders(); if (t.k === 'contacts' && contacts.length === 0) loadContacts(); if (t.k === 'blog' && blogPosts.length === 0) loadBlog(); if (t.k === 'automation' && !couponConfig) loadAutomation(); if (t.k === 'sync') loadSyncLog(); }} className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${view === t.k ? 'border-[#D4A843] text-[#D4A843]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid={`wix-tab-${t.k}`}>
                <t.icon className="w-3.5 h-3.5" />{t.l}
              </button>
            ))}
          </div>

          {/* ═══ PRODUCTS ═══ */}
          {view === 'products' && (
            <div className="space-y-4" data-testid="wix-products">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">{products.length} products on eigooinc.net</p>
                <div className="flex gap-2">
                  <Button onClick={loadProducts} variant="outline" size="sm" className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
                  <Button onClick={() => setShowCreateProduct(!showCreateProduct)} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="create-product-btn"><Plus className="w-3.5 h-3.5 mr-1" /> {showCreateProduct ? 'Cancel' : 'Add Product'}</Button>
                </div>
              </div>

              {/* Create Product Form */}
              {showCreateProduct && (
                <div className="bg-white border-2 border-[#D4A843]/30 rounded-lg p-5 animate-fade-in" data-testid="product-form">
                  <h3 className="text-xs font-bold text-[#D4A843] uppercase tracking-wider mb-3">New Product (publishes to eigooinc.net)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><label className="text-xs text-gray-600">Product Name *</label><Input value={productForm.name} onChange={e => setProductForm({...productForm, name: e.target.value})} className="mt-1 rounded-lg" placeholder="e.g. USB-C Ethernet Adapter" data-testid="product-name" /></div>
                    <div className="grid grid-cols-3 gap-2">
                      <div><label className="text-xs text-gray-600">Price ($)</label><Input value={productForm.price} onChange={e => setProductForm({...productForm, price: e.target.value})} className="mt-1 rounded-lg" placeholder="29.99" type="number" step="0.01" data-testid="product-price" /></div>
                      <div><label className="text-xs text-gray-600">SKU</label><Input value={productForm.sku} onChange={e => setProductForm({...productForm, sku: e.target.value})} className="mt-1 rounded-lg" placeholder="ETH-001" /></div>
                      <div><label className="text-xs text-gray-600">Stock Qty</label><Input value={productForm.quantity} onChange={e => setProductForm({...productForm, quantity: e.target.value})} className="mt-1 rounded-lg" placeholder="50" type="number" /></div>
                    </div>
                  </div>
                  <div className="mt-3"><label className="text-xs text-gray-600">Description</label><textarea value={productForm.description} onChange={e => setProductForm({...productForm, description: e.target.value})} className="mt-1 w-full h-20 rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="Product description..." data-testid="product-desc" /></div>
                  <div className="flex items-center gap-4 mt-3">
                    <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={productForm.visible} onChange={e => setProductForm({...productForm, visible: e.target.checked})} className="rounded" /> Visible on store</label>
                    <select value={productForm.productType} onChange={e => setProductForm({...productForm, productType: e.target.value})} className="h-8 rounded-lg border border-gray-200 px-2 text-xs bg-white"><option value="physical">Physical</option><option value="digital">Digital</option></select>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={createProduct} disabled={creatingProduct} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" data-testid="save-product-btn">
                      {creatingProduct ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5 mr-1" />} Publish to Wix
                    </Button>
                    <Button variant="outline" onClick={() => setShowCreateProduct(false)} className="rounded-lg text-xs">Cancel</Button>
                  </div>
                </div>
              )}

              {/* Product Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {products.map(p => (
                  <div key={p.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover-lift group" data-testid={`product-${p.id}`}>
                    {p.media ? (
                      <div className="h-36 bg-gray-100 overflow-hidden"><img src={p.media} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /></div>
                    ) : (
                      <div className="h-36 bg-gray-50 flex items-center justify-center"><Package className="w-8 h-8 text-gray-300" /></div>
                    )}
                    <div className="p-3">
                      <div className="flex items-start justify-between mb-1">
                        <h4 className="text-sm font-medium text-gray-900 line-clamp-2 flex-1">{p.name}</h4>
                        {p.visible ? <Eye className="w-3 h-3 text-emerald-500 shrink-0 mt-0.5" /> : <EyeOff className="w-3 h-3 text-gray-300 shrink-0 mt-0.5" />}
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm font-bold text-[#D4A843]">{p.price || '$0.00'}</span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${p.in_stock ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {p.in_stock ? (p.stock_quantity != null ? `${p.stock_quantity} in stock` : 'In Stock') : 'Out of Stock'}
                        </span>
                      </div>
                      <div className="flex gap-1 mt-3 pt-2 border-t border-gray-100">
                        <Button size="sm" variant="ghost" className="text-[10px] h-7 px-2 text-gray-500" onClick={() => setEditingProduct(editingProduct?.id === p.id ? null : { id: p.id, name: p.name, price: '' })} data-testid={`edit-${p.id}`}><Edit3 className="w-3 h-3 mr-0.5" /> Edit</Button>
                        <Button size="sm" variant="ghost" className="text-[10px] h-7 px-2 text-red-400" onClick={() => deleteProduct(p.id, p.name)} data-testid={`delete-${p.id}`}><Trash2 className="w-3 h-3 mr-0.5" /> Delete</Button>
                      </div>
                      {editingProduct?.id === p.id && (
                        <div className="mt-2 p-2 bg-gray-50 rounded-lg space-y-2 animate-fade-in">
                          <Input value={editingProduct.name} onChange={e => setEditingProduct({...editingProduct, name: e.target.value})} className="rounded text-xs h-8" placeholder="Name" />
                          <Input value={editingProduct.price} onChange={e => setEditingProduct({...editingProduct, price: e.target.value})} className="rounded text-xs h-8" placeholder="New price" type="number" step="0.01" />
                          <Button size="sm" onClick={() => updateProduct(p.id)} className="bg-[#D4A843] text-[#0A0A0A] font-semibold rounded text-[10px] h-7 w-full">Save Changes</Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {products.length === 0 && (
                  <div className="col-span-full bg-white border border-gray-200 rounded-lg p-12 text-center">
                    <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No products found. Click "Add Product" to create one.</p>
                  </div>
                )}
              </div>

              {/* Collections */}
              {collections.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">Collections</h3>
                  <div className="flex flex-wrap gap-2">
                    {collections.map(c => (
                      <span key={c.id} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700">{c.name} <span className="text-gray-400">({c.numberOfProducts})</span></span>
                    ))}
                  </div>
                </div>
              )}

              {/* Product Activity Log */}
              {productLog.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">Activity Log</h3>
                  {productLog.slice(0, 8).map(l => (
                    <div key={l.id} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                      <p className="text-[10px] text-gray-600"><span className={`font-bold ${l.action === 'created' ? 'text-emerald-600' : l.action === 'deleted' ? 'text-red-600' : 'text-blue-600'}`}>{l.action}</span> {l.name || l.wix_product_id?.slice(0, 8)} by {l.created_by_name || l.updated_by}</p>
                      <span className="text-[10px] text-gray-400">{new Date(l.timestamp).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ═══ ORDERS ═══ */}
          {view === 'orders' && (
            <div className="space-y-3" data-testid="wix-orders">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">{orders.length} orders</p>
                <Button onClick={loadOrders} variant="outline" size="sm" className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
              </div>
              {orders.map(o => (
                <div key={o.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`order-${o.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Order #{o.number}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{o.buyer_name} ({o.buyer_email})</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900">{o.total}</p>
                      <div className="flex gap-1 mt-1">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${o.payment_status === 'PAID' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{o.payment_status}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${o.fulfillment_status === 'FULFILLED' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{o.fulfillment_status || 'UNFULFILLED'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
                    <span className="text-[10px] text-gray-400">{o.items_count} items | {new Date(o.created_at).toLocaleDateString()}</span>
                    {o.fulfillment_status !== 'FULFILLED' && (
                      <Button size="sm" variant="outline" className="text-[10px] h-7 rounded-lg" onClick={() => setFulfillingOrder(fulfillingOrder === o.id ? null : o.id)} data-testid={`fulfill-${o.id}`}><Truck className="w-3 h-3 mr-0.5" /> {fulfillingOrder === o.id ? 'Cancel' : 'Fulfill'}</Button>
                    )}
                  </div>
                  {fulfillingOrder === o.id && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg flex gap-2 items-end animate-fade-in">
                      <div className="flex-1"><label className="text-[10px] text-gray-500">Tracking Number</label><Input value={fulfillForm.tracking_number} onChange={e => setFulfillForm({...fulfillForm, tracking_number: e.target.value})} className="mt-0.5 rounded text-xs h-8" placeholder="1Z999AA..." /></div>
                      <div className="w-32"><label className="text-[10px] text-gray-500">Carrier</label><Input value={fulfillForm.carrier} onChange={e => setFulfillForm({...fulfillForm, carrier: e.target.value})} className="mt-0.5 rounded text-xs h-8" placeholder="UPS" /></div>
                      <Button size="sm" onClick={() => fulfillOrder(o.id)} className="bg-emerald-600 text-white rounded text-[10px] h-8"><CheckCircle2 className="w-3 h-3 mr-0.5" /> Ship</Button>
                    </div>
                  )}
                </div>
              ))}
              {orders.length === 0 && <p className="text-center py-12 text-gray-400 bg-white rounded-lg border border-gray-200">No orders found</p>}
            </div>
          )}

          {/* ═══ CONTACTS ═══ */}
          {view === 'contacts' && (
            <div className="space-y-4" data-testid="wix-contacts">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs text-gray-500">{contacts.length} contacts in Wix CRM</p>
                <div className="flex gap-2">
                  <Button onClick={loadContacts} variant="outline" size="sm" className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
                  <Button onClick={syncContactsToLocal} disabled={syncingContacts} variant="outline" size="sm" className="rounded-lg text-xs" data-testid="sync-contacts-btn">
                    {syncingContacts ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ArrowUpRight className="w-3.5 h-3.5 mr-1" />} Wix → TechOps
                  </Button>
                  <Button onClick={syncToWix} disabled={syncingToWix} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="sync-to-wix-btn">
                    {syncingToWix ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5 mr-1" />} TechOps → Wix
                  </Button>
                </div>
              </div>

              {/* Labels */}
              {contactLabels.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {contactLabels.map(l => (
                    <span key={l.key || l.displayName} className="px-2 py-0.5 bg-[#D4A843]/10 border border-[#D4A843]/30 rounded-full text-[10px] font-medium text-[#D4A843]"><Tag className="w-2.5 h-2.5 inline mr-0.5" />{l.displayName}</span>
                  ))}
                </div>
              )}

              {/* Contacts Table */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left py-2.5 px-4 text-[10px] text-gray-500 uppercase tracking-wider font-bold">Name</th>
                    <th className="text-left py-2.5 px-4 text-[10px] text-gray-500 uppercase tracking-wider font-bold">Email</th>
                    <th className="text-left py-2.5 px-4 text-[10px] text-gray-500 uppercase tracking-wider font-bold">Phone</th>
                    <th className="text-left py-2.5 px-4 text-[10px] text-gray-500 uppercase tracking-wider font-bold">Company</th>
                    <th className="text-right py-2.5 px-4 text-[10px] text-gray-500 uppercase tracking-wider font-bold">Last Active</th>
                  </tr></thead>
                  <tbody>
                    {contacts.map(c => (
                      <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`contact-${c.id}`}>
                        <td className="py-2.5 px-4 font-medium text-gray-900">{c.name || '—'}</td>
                        <td className="py-2.5 px-4 text-gray-600">{c.email || '—'}</td>
                        <td className="py-2.5 px-4 text-gray-600 font-mono">{c.phone || '—'}</td>
                        <td className="py-2.5 px-4 text-gray-500">{c.company || '—'}</td>
                        <td className="py-2.5 px-4 text-gray-400 text-right">{c.last_activity ? new Date(c.last_activity).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {contacts.length === 0 && <p className="text-center py-8 text-gray-400 text-xs">No contacts found</p>}
              </div>
            </div>
          )}

          {/* ═══ BLOG ═══ */}
          {view === 'blog' && (
            <div className="space-y-4" data-testid="wix-blog">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">{blogPosts.length} blog posts</p>
                <div className="flex gap-2">
                  <Button onClick={loadBlog} variant="outline" size="sm" className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
                  <Button onClick={() => setShowBlogForm(!showBlogForm)} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="create-post-btn"><Plus className="w-3.5 h-3.5 mr-1" /> {showBlogForm ? 'Cancel' : 'New Post'}</Button>
                </div>
              </div>

              {/* Blog Form */}
              {showBlogForm && (
                <div className="bg-white border-2 border-[#D4A843]/30 rounded-lg p-5 animate-fade-in" data-testid="blog-form">
                  <h3 className="text-xs font-bold text-[#D4A843] uppercase tracking-wider mb-3">New Blog Post (pushes to eigooinc.net)</h3>
                  <div><label className="text-xs text-gray-600">Title *</label><Input value={blogForm.title} onChange={e => setBlogForm({...blogForm, title: e.target.value})} className="mt-1 rounded-lg" placeholder="e.g. 5 Tips to Secure Your Home Network" data-testid="blog-title" /></div>
                  <div className="mt-3"><label className="text-xs text-gray-600">Content</label><textarea value={blogForm.content} onChange={e => setBlogForm({...blogForm, content: e.target.value})} className="mt-1 w-full h-32 rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="Write your blog content..." data-testid="blog-content" /></div>
                  <div className="flex items-center gap-4 mt-3">
                    <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={blogForm.publish} onChange={e => setBlogForm({...blogForm, publish: e.target.checked})} className="rounded" data-testid="blog-publish" /> Publish immediately</label>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={createBlogPost} disabled={creatingPost} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" data-testid="save-post-btn">
                      {creatingPost ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} {blogForm.publish ? 'Publish' : 'Save Draft'}
                    </Button>
                    <Button variant="outline" onClick={() => setShowBlogForm(false)} className="rounded-lg text-xs">Cancel</Button>
                  </div>
                </div>
              )}

              {/* Blog Posts List */}
              <div className="space-y-3">
                {blogPosts.map(p => (
                  <div key={p.id} className="bg-white border border-gray-200 rounded-lg p-4 flex gap-4" data-testid={`post-${p.id}`}>
                    {p.cover_image && <div className="w-20 h-20 rounded-lg bg-gray-100 overflow-hidden shrink-0"><img src={p.cover_image} alt="" className="w-full h-full object-cover" /></div>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${p.status === 'PUBLISHED' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{p.status}</span>
                        {p.featured && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#D4A843]/20 text-[#D4A843]">Featured</span>}
                      </div>
                      <h4 className="text-sm font-medium text-gray-900 truncate">{p.title}</h4>
                      {p.excerpt && <p className="text-[10px] text-gray-500 mt-0.5 line-clamp-2">{p.excerpt}</p>}
                      <p className="text-[10px] text-gray-400 mt-1">{p.published_date ? new Date(p.published_date).toLocaleDateString() : 'Draft'}</p>
                    </div>
                    {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#D4A843] hover:underline shrink-0 self-start mt-1"><ArrowUpRight className="w-3 h-3" /></a>}
                  </div>
                ))}
                {blogPosts.length === 0 && <p className="text-center py-12 text-gray-400 bg-white rounded-lg border border-gray-200">No blog posts. Create one to push content to eigooinc.net.</p>}
              </div>
            </div>
          )}

          {/* ═══ SEO OPTIMIZER ═══ */}
          {view === 'seo' && (
            <div className="space-y-5" data-testid="wix-seo">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#D4A843]" /> SEO Optimizer</h2>
                <Button onClick={runSeoAudit} disabled={auditLoading} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="run-audit-btn">
                  {auditLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <BarChart3 className="w-3.5 h-3.5 mr-1" />} Run SEO Audit
                </Button>
              </div>

              {/* Audit Results */}
              {seoAudit && (
                <>
                  {/* Score Overview */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="seo-scores">
                    <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                      <p className={`text-3xl font-bold ${seoAudit.average_score >= 70 ? 'text-emerald-600' : seoAudit.average_score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{seoAudit.average_score}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">Avg SEO Score</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-gray-900">{seoAudit.total_products}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">Products</p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-red-600">{seoAudit.critical_issues}</p>
                      <p className="text-[10px] text-red-600 mt-0.5">Critical Issues</p>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-amber-600">{seoAudit.warning_issues}</p>
                      <p className="text-[10px] text-amber-600 mt-0.5">Warnings</p>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-emerald-600">{seoAudit.score_distribution?.excellent || 0}</p>
                      <p className="text-[10px] text-emerald-600 mt-0.5">Excellent (80+)</p>
                    </div>
                  </div>

                  {/* Bulk Rewrite */}
                  <div className="flex gap-2 items-center">
                    <Button onClick={runBulkRewrite} disabled={bulkRewriting} variant="outline" className="rounded-lg text-xs" data-testid="bulk-rewrite-btn">
                      {bulkRewriting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />} AI Rewrite Worst 10
                    </Button>
                    <span className="text-[10px] text-gray-400">Generates optimized titles & descriptions for lowest-scoring products</span>
                  </div>

                  {/* Bulk Rewrite Results */}
                  {bulkRewrites.length > 0 && (
                    <div className="bg-white border-2 border-[#D4A843]/30 rounded-lg p-5" data-testid="bulk-rewrites">
                      <h3 className="text-xs font-bold text-[#D4A843] uppercase tracking-wider mb-3">AI Rewrites ({bulkRewrites.length})</h3>
                      <div className="space-y-3">{bulkRewrites.map((r, i) => (
                        <div key={r.product_id || i} className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 line-through">{r.original_name} <span className="text-[10px]">(Score: {r.original_score})</span></p>
                          {r.optimized ? (
                            <>
                              <p className="text-sm font-medium text-gray-900 mt-1">{r.optimized.optimized_title}</p>
                              <p className="text-[10px] text-gray-600 mt-0.5">{r.optimized.optimized_description}</p>
                              <div className="flex gap-2 mt-2">
                                <Button size="sm" onClick={() => applySeoRewrite(r.product_id)} disabled={applying[r.product_id]} className="bg-emerald-600 text-white rounded text-[10px] h-7">
                                  {applying[r.product_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-0.5" />} Apply to Wix
                                </Button>
                              </div>
                            </>
                          ) : <p className="text-[10px] text-red-500">{r.error}</p>}
                        </div>
                      ))}</div>
                    </div>
                  )}

                  {/* Product List */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="seo-products">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left py-2 px-3 text-[10px] text-gray-500 uppercase font-bold">Product</th>
                        <th className="text-center py-2 px-3 text-[10px] text-gray-500 uppercase font-bold w-16">Score</th>
                        <th className="text-center py-2 px-3 text-[10px] text-gray-500 uppercase font-bold w-20">Issues</th>
                        <th className="text-center py-2 px-3 text-[10px] text-gray-500 uppercase font-bold w-16">Img</th>
                        <th className="text-right py-2 px-3 text-[10px] text-gray-500 uppercase font-bold w-28">Action</th>
                      </tr></thead>
                      <tbody>{seoAudit.products?.slice(0, 30).map(p => (
                        <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`seo-product-${p.id}`}>
                          <td className="py-2 px-3">
                            <p className="font-medium text-gray-900 truncate max-w-xs">{p.name || '(no title)'}</p>
                            {p.issues?.filter(i => i.severity === 'critical').map((issue, idx) => (
                              <span key={`idx-${idx}`} className="text-[9px] text-red-500 flex items-center gap-0.5 mt-0.5"><AlertTriangle className="w-2.5 h-2.5" />{issue.issue}</span>
                            ))}
                            {rewrites[p.id] && (
                              <div className="mt-1.5 p-2 bg-emerald-50 rounded">
                                <p className="text-[10px] font-bold text-emerald-700">{rewrites[p.id].optimized_title}</p>
                                <p className="text-[9px] text-emerald-600">{rewrites[p.id].optimized_description?.slice(0, 100)}...</p>
                                <Button size="sm" onClick={() => applySeoRewrite(p.id)} disabled={applying[p.id]} className="bg-emerald-600 text-white rounded text-[9px] h-6 mt-1 px-2">
                                  {applying[p.id] ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : 'Apply to Wix'}
                                </Button>
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <span className={`font-bold ${p.score >= 70 ? 'text-emerald-600' : p.score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{p.score}</span>
                          </td>
                          <td className="py-2 px-3 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${p.issue_count === 0 ? 'bg-emerald-100 text-emerald-700' : p.issue_count <= 2 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{p.issue_count}</span>
                          </td>
                          <td className="py-2 px-3 text-center">{p.has_image ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mx-auto" /> : <XCircle className="w-3.5 h-3.5 text-red-400 mx-auto" />}</td>
                          <td className="py-2 px-3 text-right">
                            <Button size="sm" variant="ghost" disabled={rewriting[p.id]} onClick={() => rewriteProduct(p.id, p.name, '')} className="text-[10px] h-6 px-2">
                              {rewriting[p.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3 mr-0.5" />} AI Rewrite
                            </Button>
                          </td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </>
              )}

              {!seoAudit && !auditLoading && (
                <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                  <TrendingUp className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">Click "Run SEO Audit" to analyze all eigooinc.net products.</p>
                  <p className="text-xs text-gray-400 mt-1">Get SEO scores, identify issues, and AI-optimize titles & descriptions.</p>
                </div>
              )}

              {/* Blog SEO Generator */}
              <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="blog-seo">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5 text-[#D4A843]" /> SEO Blog Generator</h3>
                <p className="text-[10px] text-gray-500 mb-3">AI writes SEO-optimized blog posts for eigooinc.net. Drives organic traffic.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div><label className="text-[10px] text-gray-500">Topic *</label><Input value={blogTopic} onChange={e => setBlogTopic(e.target.value)} className="mt-0.5 rounded-lg" placeholder="e.g. 5 Signs Your Network Needs an Upgrade" data-testid="seo-blog-topic" /></div>
                  <div><label className="text-[10px] text-gray-500">Keywords (comma separated)</label><Input value={blogKeywords} onChange={e => setBlogKeywords(e.target.value)} className="mt-0.5 rounded-lg" placeholder="network security, IT tips, cybersecurity" data-testid="seo-blog-keywords" /></div>
                </div>
                <Button onClick={generateBlog} disabled={generatingBlog} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="generate-blog-btn">
                  {generatingBlog ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />} Generate Blog Post
                </Button>

                {generatedBlog && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid="generated-blog">
                    <h4 className="text-sm font-bold text-gray-900 mb-1">{generatedBlog.title}</h4>
                    <p className="text-[10px] text-gray-500 mb-2 italic">{generatedBlog.meta_description}</p>
                    <div className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto">{generatedBlog.content}</div>
                    {generatedBlog.tags && <div className="flex gap-1 mt-2">{generatedBlog.tags.map(t => <span key={t} className="px-2 py-0.5 bg-[#D4A843]/10 text-[#D4A843] rounded-full text-[10px]">{t}</span>)}</div>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ═══ AUTOMATION ═══ */}
          {view === 'automation' && (
            <div className="space-y-5" data-testid="wix-automation">
              {/* Auto-Coupon Config */}
              {couponConfig && (
                <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="coupon-config">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-1.5"><Percent className="w-3.5 h-3.5 text-[#D4A843]" /> Auto-Coupon Engine</h3>
                  <p className="text-[10px] text-gray-500 mb-3">Auto-generate discount coupons on ticket resolve. Percentage-based with profit protection — caps at break-even factoring shipping.</p>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
                      <input type="checkbox" checked={couponConfig.enabled || false} onChange={e => setCouponConfig({...couponConfig, enabled: e.target.checked})} className="rounded" data-testid="coupon-enabled" /> Enable auto-coupons
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div><label className="text-[10px] text-gray-500">Discount %</label><Input value={couponConfig.discount_percent || 10} onChange={e => setCouponConfig({...couponConfig, discount_percent: Number(e.target.value)})} className="mt-0.5 rounded-lg" type="number" data-testid="discount-pct" /></div>
                      <div><label className="text-[10px] text-gray-500">Max $ Cap</label><Input value={couponConfig.max_discount_dollars || 50} onChange={e => setCouponConfig({...couponConfig, max_discount_dollars: Number(e.target.value)})} className="mt-0.5 rounded-lg" type="number" /></div>
                      <div><label className="text-[10px] text-gray-500">Shipping Estimate ($)</label><Input value={couponConfig.shipping_cost_estimate || 8} onChange={e => setCouponConfig({...couponConfig, shipping_cost_estimate: Number(e.target.value)})} className="mt-0.5 rounded-lg" type="number" step="0.01" /></div>
                      <div><label className="text-[10px] text-gray-500">Margin Floor %</label><Input value={couponConfig.profit_margin_floor_percent || 5} onChange={e => setCouponConfig({...couponConfig, profit_margin_floor_percent: Number(e.target.value)})} className="mt-0.5 rounded-lg" type="number" /></div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div><label className="text-[10px] text-gray-500">Min Priority</label>
                        <select value={couponConfig.min_ticket_priority || 'high'} onChange={e => setCouponConfig({...couponConfig, min_ticket_priority: e.target.value})} className="mt-0.5 h-9 w-full rounded-lg border border-gray-200 px-3 text-xs bg-white"><option value="low">Low+</option><option value="medium">Medium+</option><option value="high">High+</option><option value="critical">Critical only</option></select>
                      </div>
                      <div><label className="text-[10px] text-gray-500">Expiry (days)</label><Input value={couponConfig.expiry_days || 30} onChange={e => setCouponConfig({...couponConfig, expiry_days: Number(e.target.value)})} className="mt-0.5 rounded-lg" type="number" /></div>
                      <div><label className="text-[10px] text-gray-500">Code Prefix</label><Input value={couponConfig.coupon_prefix || 'TECHOPS'} onChange={e => setCouponConfig({...couponConfig, coupon_prefix: e.target.value})} className="mt-0.5 rounded-lg" /></div>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" checked={couponConfig.one_per_customer !== false} onChange={e => setCouponConfig({...couponConfig, one_per_customer: e.target.checked})} className="rounded" /> One active coupon per customer</label>
                    <Button onClick={saveCouponConfig} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" data-testid="save-coupon-config"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Save Coupon Config</Button>
                  </div>

                  {/* Generated Coupons */}
                  {generatedCoupons.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Generated Coupons ({generatedCoupons.length})</h4>
                      <div className="space-y-1.5">{generatedCoupons.slice(0, 8).map(c => (
                        <div key={c.id} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <code className="text-xs font-bold text-[#D4A843] bg-blue-50 px-2 py-0.5 rounded">{c.code}</code>
                            <span className="text-[10px] text-gray-500">{c.discount_value}% off (max ${c.max_discount_dollars})</span>
                            {c.used && <span className="text-[10px] text-red-500 font-bold">USED</span>}
                          </div>
                          <span className="text-[10px] text-gray-400">{c.customer_email || 'N/A'} | {new Date(c.generated_at).toLocaleDateString()}</span>
                        </div>
                      ))}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Order-to-Dispatch Pipeline */}
              {dispatchConfig && (
                <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="dispatch-config">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-[#D4A843]" /> Order-to-Dispatch Pipeline</h3>
                  <p className="text-[10px] text-gray-500 mb-3">Auto-create dispatch tickets from Wix orders matching TechOps keywords or originating from TechOps clicks.</p>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
                      <input type="checkbox" checked={dispatchConfig.enabled || false} onChange={e => setDispatchConfig({...dispatchConfig, enabled: e.target.checked})} className="rounded" data-testid="dispatch-enabled" /> Enable dispatch pipeline
                    </label>
                    <div>
                      <label className="text-[10px] text-gray-500">Filter Keywords (comma separated)</label>
                      <Input value={(dispatchConfig.filter_keywords || []).join(', ')} onChange={e => setDispatchConfig({...dispatchConfig, filter_keywords: e.target.value.split(',').map(k => k.trim().toLowerCase()).filter(Boolean)})} className="mt-0.5 rounded-lg text-xs" placeholder="techops, ethernet, adapter, router, ssd, laptop" data-testid="dispatch-keywords" />
                      <p className="text-[10px] text-gray-400 mt-0.5">Orders matching these keywords auto-create dispatch tickets</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="text-[10px] text-gray-500">Default Priority</label>
                        <select value={dispatchConfig.default_priority || 'medium'} onChange={e => setDispatchConfig({...dispatchConfig, default_priority: e.target.value})} className="mt-0.5 h-9 w-full rounded-lg border border-gray-200 px-3 text-xs bg-white"><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select>
                      </div>
                      <div className="flex items-end gap-2">
                        <label className="flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" checked={dispatchConfig.auto_create_ticket !== false} onChange={e => setDispatchConfig({...dispatchConfig, auto_create_ticket: e.target.checked})} className="rounded" /> Auto-create tickets</label>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={saveDispatchConfig} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" data-testid="save-dispatch-config"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Save Config</Button>
                      <Button onClick={processOrders} disabled={processingOrders} variant="outline" className="rounded-lg text-xs" data-testid="process-orders-btn">
                        {processingOrders ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />} Process Orders Now
                      </Button>
                    </div>
                  </div>

                  {dispatchLog.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Dispatch Log ({dispatchLog.length})</h4>
                      {dispatchLog.slice(0, 6).map(l => (
                        <div key={l.id} className="flex items-center justify-between py-1.5 text-[10px]">
                          <span className="text-gray-700">Order #{l.wix_order_number} → Ticket {l.id?.slice(0, 8)}</span>
                          <span className="text-gray-400">{new Date(l.created_at).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Supplier CSV Import */}
              <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="csv-import">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-1.5"><FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" /> Supplier CSV Import</h3>
                <p className="text-[10px] text-gray-500 mb-3">Bulk-import products from a CSV file. Products publish directly to eigooinc.net.</p>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Button onClick={loadCsvTemplate} variant="outline" size="sm" className="rounded-lg text-[10px]" data-testid="load-template"><Download className="w-3 h-3 mr-1" /> Load Template</Button>
                    <span className="text-[10px] text-gray-400 self-center">Columns: name (required), price, sku, quantity, description</span>
                  </div>
                  <textarea value={csvText} onChange={e => setCsvText(e.target.value)} className="w-full h-32 rounded-lg border border-gray-200 px-3 py-2 text-xs font-mono bg-gray-50" placeholder="name,price,sku,quantity,description&#10;USB-C Ethernet Adapter,29.99,ETH-001,50,Gigabit adapter" data-testid="csv-input" />
                  <Button onClick={importCsv} disabled={importing} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="import-csv-btn">
                    {importing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5 mr-1" />} Import to Wix Store
                  </Button>
                </div>

                {importLog.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Import History</h4>
                    {importLog.slice(0, 5).map(l => (
                      <div key={l.id} className="flex items-center justify-between py-1.5 text-[10px]">
                        <span className="text-gray-700">
                          {l.total_rows} rows → <span className="text-emerald-600 font-bold">{l.created} created</span>
                          {l.failed > 0 && <span className="text-red-500 font-bold ml-1">{l.failed} failed</span>}
                        </span>
                        <span className="text-gray-400">{l.imported_by_name} | {new Date(l.timestamp).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ═══ SYNC LOG ═══ */}
          {view === 'sync' && (
            <div className="space-y-4" data-testid="wix-sync">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">Sync activity log</p>
                <Button onClick={loadSyncLog} variant="outline" size="sm" className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                {syncLog.length > 0 ? syncLog.map(l => (
                  <div key={l.id} className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="text-xs text-gray-900 flex items-center gap-1.5">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${l.type?.includes('to_wix') ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>{l.type?.replace(/_/g, ' ')}</span>
                        Synced: <span className="font-bold text-emerald-600">{l.synced}</span> | Skipped: {l.skipped}
                      </p>
                      {l.errors?.length > 0 && <p className="text-[10px] text-red-500 mt-0.5">Errors: {l.errors.slice(0, 2).join(', ')}</p>}
                    </div>
                    <span className="text-[10px] text-gray-400">{new Date(l.timestamp).toLocaleString()}</span>
                  </div>
                )) : <p className="text-center py-8 text-gray-400 text-xs">No sync activity yet</p>}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
